import type { CriterionOutcome, Event, Laag, Section, SectionType } from '@eformulieren/model'
import { laagOf } from './projection.js'
import { type State, singleValueOf } from './state.js'
import { returningFields } from './werkverzameling.js'

/**
 * Wat een overdracht vraagt voordat hij mag (ontwerp 7.8 voorstel 1, R-181, R-182).
 *
 * **Waarom dit in de engine staat en niet alleen in de pod.** Tot de toetsknoppen stond het als
 * `HANDOVERS` in `apps/runtime/src/overdracht.ts`, en dat was genoeg zolang alleen de pod een
 * toetsbeurt sloot, via een machinale aanroep. Sinds de toetsgedaante zelf knoppen heeft stelt
 * de renderer dezelfde vraag: mag goedkeuren nu? Die regel twee keer opschrijven is precies wat
 * ontwerp 5.2 verbiedt, en dezelfde stap die M5.13a met `BEURTEN` zette. De pod blijft het gezag:
 * hij rekent dit na met dezelfde functie, en een knop die te vroeg aan staat levert hooguit een
 * weigering op, nooit een revisie die er niet hoort.
 *
 * Wiens beurt een gebeurtenis sluit staat in `BEURTEN` (`beurt.ts`); hier staat alleen wat er
 * over de criteria moet gelden.
 */
export interface HandoverPolicy {
  /** Of elk criterium van de laag die sluit een menselijk oordeel moet hebben (7.8 voorstel 1). */
  readonly vraagtVolledigOordeel: boolean
  /** Of er minstens één criterium moet zijn dat niet voldoet (7.8 voorstel 1, R-182). */
  readonly vraagtOpenstaandCriterium: boolean
  /**
   * Of elke sectie die aanpassing vraagt een ingevulde opmerking voor de aanvrager moet hebben,
   * waar de toetslaag daar een teruggaand opmerkingveld voor heeft (R-75, R-132). Zie
   * {@link blockerForHandover}.
   */
  readonly vraagtOpmerking: boolean
  /**
   * Of elk criterium moet voldoen (besluit van 30 oktober 2026, R-181). Alleen goedkeuren: in
   * behandeling nemen terwijl een criterium niet voldoet stuurt een tegenstrijdige uitslag naar het
   * processysteem. De toetser zet het criterium dan eerst op voldoet, of legt terug of keurt af.
   */
  readonly vraagtAllesVoldoet: boolean
}

/**
 * Het beleid per gebeurtenis van R-173.
 *
 * **Elke overdracht vraagt een menselijk oordeel op elk criterium van de laag die sluit.** Voor
 * de toetser stond dat er al (7.8 voorstel 1). Sinds het besluit van 9 oktober 2026 geldt het ook
 * voor de drie overdrachten van de invoerlaag: een beoordelaar dient pas in als elk criterium in
 * zijn beoordelingsformulier een oordeel heeft (R-147, R-152, R-187). Alle drie en niet alleen
 * `ingediend`, want dezelfde knop geeft per instantie een ander woord (`eventOnSubmit`).
 * Een aanvraagformulier merkt er niets van: de criteria daarvan staan in de toetslaag, en wie de
 * invoerbeurt sluit krijgt alleen de criteria van de invoerlaag mee (`criteriumIdsIn`), en dat
 * zijn er daar geen.
 *
 * Terugleggen vraagt daarbovenop iets om te corrigeren, want zonder dat belandt de beurt zonder
 * reden bij de invuller, en een opmerking die de invuller vertelt wat (R-75, R-132).
 */
export const HANDOVER_POLICY = {
  ingediend: { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: false, vraagtOpmerking: false, vraagtAllesVoldoet: false },
  teruggelegd: { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: true, vraagtOpmerking: true, vraagtAllesVoldoet: false },
  aangevuld: { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: false, vraagtOpmerking: false, vraagtAllesVoldoet: false },
  'opnieuw-ingediend': { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: false, vraagtOpmerking: false, vraagtAllesVoldoet: false },
  goedgekeurd: { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: false, vraagtOpmerking: false, vraagtAllesVoldoet: true },
  afgekeurd: { vraagtVolledigOordeel: true, vraagtOpenstaandCriterium: false, vraagtOpmerking: false, vraagtAllesVoldoet: false },
} as const satisfies Record<Event, HandoverPolicy>

/**
 * De drie uitkomsten van de toets, in de volgorde van de knoppen (R-181, ontwerp 7.3).
 *
 * Gelegenheid tot correctie, definitief niet in behandeling, in behandeling: dezelfde volgorde
 * als het aangeboden scherm W027 en het look-and-feel-canvas, met de primaire handeling achteraan.
 * Dat het precies de gebeurtenissen zijn die de toetsbeurt sluiten bewaakt de test, tegen
 * `BEURTEN`.
 */
export const REVIEW_HANDOVERS = ['teruggelegd', 'afgekeurd', 'goedgekeurd'] as const
export type ReviewHandover = (typeof REVIEW_HANDOVERS)[number]

/** Waarom een overdracht nu niet mag, met de code die de pod er ook voor geeft. */
export type HandoverBlocker =
  | { readonly code: 'oordeel-ontbreekt'; readonly criteria: readonly string[] }
  | { readonly code: 'niets-om-te-corrigeren' }
  /** `criteria` zijn de criteria die niet voldoen; goedkeuren wacht daarop (besluit van 30 oktober 2026). */
  | { readonly code: 'criterium-voldoet-niet'; readonly criteria: readonly string[] }
  /** `secties` zijn de secties van de aanvrager die aanpassing vragen en nog geen opmerking hebben. */
  | { readonly code: 'opmerking-ontbreekt'; readonly secties: readonly string[] }

/**
 * Per sectie van de aanvrager: welke criteria erover oordelen en welke opmerkingvelden van de
 * toetslaag er naartoe teruggaan (R-75, R-109, D-55).
 */
export interface Handback {
  /** De sectie van de aanvrager, uit `hoortBij` van de toetssectie. */
  readonly overSectie: string
  /** De criteriumIds in de toetssecties die over deze sectie gaan. */
  readonly criteria: readonly string[]
  /** De opmerkingvelden met `gaatTerug` in die toetssecties; nooit leeg. */
  readonly veldIds: readonly string[]
}

/** Wat de regel over de opmerking nodig heeft: welke velden teruggaan, en wat erin staat. */
export interface Comments {
  /** Uit {@link handbacksIn}, over de definitie (pod) of de projectie (renderer). */
  readonly teruggaven: readonly Handback[]
  /** De stand van de beurt die sluit, met in elk geval de toetslaag erin. */
  readonly stand: State
}

/**
 * De teruggaven van deze secties: per sectie van de aanvrager de criteria en de opmerkingvelden
 * die erover gaan (R-75).
 *
 * **Welke velden teruggaan komt uit `returningFields`**, dezelfde functie die bepaalt welke
 * opmerking de aanvrager te zien krijgt (`commentsFrom`). Een `gaatTerug`-veld in een
 * toetssectie zonder `hoortBij` komt nergens aan, en telt hier dus ook niet: anders zou een
 * ingevuld veld de regel halen terwijl de aanvrager niets ziet, en dat is precies de fout die
 * deze regel moet voorkomen.
 *
 * Een sectie zonder teruggaand opmerkingveld staat er niet in: daar is niets om in te vullen.
 */
export function handbacksIn(sections: readonly Section[]): readonly Handback[] {
  const perSection = new Map<string, { criteria: string[]; veldIds: string[] }>()
  for (const { veldId: fieldId, overSectie: aboutSection } of returningFields({ secties: sections })) {
    const handback = perSection.get(aboutSection) ?? { criteria: [], veldIds: [] }
    handback.veldIds.push(fieldId)
    perSection.set(aboutSection, handback)
  }
  for (const section of sections) {
    if (section.type !== 'toets' || section.hoortBij === undefined) continue
    const handback = perSection.get(section.hoortBij)
    if (handback === undefined) continue
    for (const field of section.velden) {
      if (field.control === 'criterium') handback.criteria.push(field.criteriumId)
    }
  }
  return [...perSection].map(([aboutSection, { criteria, veldIds: fieldIds }]) => ({ overSectie: aboutSection, criteria, veldIds: fieldIds }))
}

/**
 * Het veld waarin een machinale opmerking voor deze sectie van de aanvrager landt, of niets
 * (R-75, ontwerp 7.8 voorstel 2).
 *
 * Een machinale aanroeper heeft geen toegang tot de samenwerklaag en stuurt de opmerking daarom
 * per sectie mee in het verzoek; de pod schrijft haar dan zelf in het formulier. Per sectie, want
 * dat is de eenheid waarin de aanvrager de opmerking ziet (R-132) en waarin de regel hierboven
 * telt. Heeft de toetslaag voor die sectie meer dan één teruggaand veld (R-75 staat velden per
 * criterium toe), dan het eerste in de volgorde van de definitie: elk van die velden haalt de
 * regel, en de aanvrager ziet ze alle.
 */
export function fieldForComment(handbacks: readonly Handback[], aboutSection: string): string | undefined {
  return handbacks.find((handback) => handback.overSectie === aboutSection)?.veldIds[0]
}

/**
 * Staat er in dit veld tekst die de aanvrager te zien krijgt?
 *
 * Dezelfde lezing als `commentsFrom` in `werkverzameling.ts`: een tekstwaarde die na het
 * weghalen van witruimte niet leeg is. Zo geldt per constructie: mag terugleggen, dan ziet de
 * aanvrager een opmerking.
 */
function filled(state: State, fieldId: string): boolean {
  const value = singleValueOf(state[fieldId])
  return typeof value === 'string' && value.trim() !== ''
}

/**
 * Wat deze overdracht nu tegenhoudt, of niets.
 *
 * `criteria` zijn de criteriumIds van de laag die sluit ({@link criterionIdsIn}); `uitkomsten`
 * zijn de uitkomsten van deze beurt.
 *
 * **Alleen een menselijk oordeel telt.** Een uitkomst met alleen een machine-deel is voorwerk
 * (R-180) en geen oordeel. Tot deze opdracht telde de pod elke uitkomst-rij als beoordeeld, en
 * de automatische controles schrijven bij het eerste oordeel een rij voor elk criterium met een
 * aangewezen controle (`oordeel.ts`): dan kon een toetsbeurt met twee criteria sluiten na één
 * oordeel. Het voorbeeldformulier heeft één criterium en liet het daarom niet zien.
 *
 * **Terugleggen vraagt een opmerking voor de aanvrager, per sectie die aanpassing vraagt**
 * (R-75, R-132). R-132 zegt wat de aanvrager bij aanvullen ziet: "alleen de secties die
 * aanpassing vragen, met de opmerking van NWO". R-75 zegt dat die opmerking geen vanzelfsprekende
 * voorziening is maar configuratie: in de toetslaag staat welke opmerkingvelden er per criterium
 * of sectie zijn en dat ze teruggaan. Samen geeft dat deze regel: een sectie vraagt aanpassing
 * als een criterium in een toetssectie die over haar gaat (`hoortBij`) niet voldoet, en heeft de
 * toetslaag voor die sectie een teruggaand opmerkingveld, dan moet er daar minstens één van zijn
 * ingevuld. Per sectie en niet "ergens in de toetslaag": een opmerking bij een sectie die geen
 * aanpassing vraagt staat bij een sectie waar de aanvrager niet naar wordt gestuurd, en helpt
 * hem bij de sectie die het wel vraagt niet. Bij één toetssectie, zoals in het voorbeeldformulier,
 * is dat hetzelfde als "minstens één ingevuld".
 *
 * **Waar de toetslaag geen teruggaand opmerkingveld heeft, houdt de regel niets tegen.** Dat de
 * opmerking teruggaat is volgens R-75 een keuze van de inrichter; zonder die keuze is er in het
 * formulier geen plek voor zo'n opmerking, en de regel zou terugleggen dan onmogelijk maken. Wat
 * er wel altijd teruggaat is de onderbouwing per afgewezen criterium, gestructureerd in de
 * uitkomst van het bericht (R-182, R-187), zodat het processysteem er een verzoek tot correctie
 * mee kan vullen. De aanvrager ziet dan in het formulier de gemarkeerde sectie zonder tekst.
 */
export function blockerForHandover(
  event: Event,
  criteria: readonly string[],
  outcomes: readonly CriterionOutcome[],
  comments: Comments,
): HandoverBlocker | undefined {
  const policy = HANDOVER_POLICY[event]
  if (policy.vraagtVolledigOordeel) {
    const assessed = new Set(
      outcomes.filter((outcome) => outcome.oordeel !== undefined).map((outcome) => outcome.criteriumId),
    )
    // Gesorteerd, want de volgorde van de definitie en die van de opslag kunnen verschillen, en
    // dezelfde invoer hoort in Node en in de browser dezelfde melding te geven (5.9).
    const missing = criteria.filter((id) => !assessed.has(id)).sort()
    if (missing.length > 0) return { code: 'oordeel-ontbreekt', criteria: missing }
  }
  if (policy.vraagtAllesVoldoet) {
    const own = new Set(criteria)
    const failing = outcomes
      .filter((outcome) => own.has(outcome.criteriumId) && outcome.oordeel !== undefined && !outcome.oordeel.voldoet)
      .map((outcome) => outcome.criteriumId)
      .sort()
    if (failing.length > 0) return { code: 'criterium-voldoet-niet', criteria: failing }
  }
  if (policy.vraagtOpenstaandCriterium) {
    const anyFailing = outcomes.some((outcome) => outcome.oordeel !== undefined && !outcome.oordeel.voldoet)
    if (!anyFailing) return { code: 'niets-om-te-corrigeren' }
  }
  if (policy.vraagtOpmerking) {
    const rejected = new Set(
      outcomes
        .filter((outcome) => outcome.oordeel !== undefined && !outcome.oordeel.voldoet)
        .map((outcome) => outcome.criteriumId),
    )
    const without = comments.teruggaven
      .filter((handback) => handback.criteria.some((id) => rejected.has(id)))
      .filter((handback) => !handback.veldIds.some((fieldId) => filled(comments.stand, fieldId)))
      .map((handback) => handback.overSectie)
      .sort()
    if (without.length > 0) return { code: 'opmerking-ontbreekt', secties: without }
  }
  return undefined
}

/**
 * De criteriumIds in deze secties die bij deze laag horen (R-152, R-110).
 *
 * Over secties en niet over een definitie, zodat de pod (de bevroren definitie) en de renderer
 * (de projectie) dezelfde functie gebruiken. De laag komt uit het sectietype, net als bij
 * `criteriaMetLaag`.
 */
export function criterionIdsIn(
  sections: readonly { readonly type: SectionType; readonly velden: readonly { readonly control: string }[] }[],
  laag: Laag,
): readonly string[] {
  const out: string[] = []
  for (const section of sections) {
    if (laagOf(section.type) !== laag) continue
    for (const field of section.velden) {
      if (field.control === 'criterium' && 'criteriumId' in field && typeof field.criteriumId === 'string') {
        out.push(field.criteriumId)
      }
    }
  }
  return out
}
